import { InteractiveMapBlockConfiguration, InteractiveMapBlockElements } from '@features/Components/InteractiveMapBlock/Scripts/interactiveMapBlock.types';
import useInfoPointDirection from '@features/Components/InteractiveMapBlock/Scripts/useInfoPointDirection';
import useInteractiveMapState from '@features/Components/InteractiveMapBlock/Scripts/useInteractiveMapState';

type UseInteractiveMapInteractionsParameters = {
    configuration: InteractiveMapBlockConfiguration;
    directionController: ReturnType<typeof useInfoPointDirection>;
    interactiveElements: InteractiveMapBlockElements;
    panZoomController: {
        isActivationSuppressed: () => boolean;
    };
    state: ReturnType<typeof useInteractiveMapState>;
};

const useInteractiveMapInteractions = ({
    configuration,
    directionController,
    interactiveElements,
    panZoomController,
    state,
}: UseInteractiveMapInteractionsParameters): void => {
    const canUseHoverInteractions = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const shouldIgnoreActivation = (): boolean => {
        return panZoomController.isActivationSuppressed();
    };

    const syncHoveredNumber = (number: string): void => {
        if (state.getActiveNumber() === number) {
            return;
        }

        directionController.updateDirection(number);
        state.highlightNumber(number);
    };

    const toggleNumber = (number: string): void => {
        if (shouldIgnoreActivation()) {
            return;
        }

        if (state.getActiveNumber() === number) {
            state.clearActiveNumbers();

            if (canUseHoverInteractions) {
                state.highlightNumber(number);
                directionController.updateDirection(number);
            }

            return;
        }

        state.activateNumber(number);
        directionController.updateDirection(number);
    };

    interactiveElements.infoListButtonElements.forEach((infoListButtonElement) => {
        const number = infoListButtonElement.dataset.number;

        if (!number) {
            return;
        }

        infoListButtonElement.addEventListener('focus', () => {
            syncHoveredNumber(number);
        });

        infoListButtonElement.addEventListener('blur', () => {
            state.clearHighlightedNumbers();
        });

        infoListButtonElement.addEventListener('click', (event) => {
            event.stopPropagation();
            toggleNumber(number);
        });
    });

    if (canUseHoverInteractions) {
        interactiveElements.infoListItemElements.forEach((infoListItemElement) => {
            const number = infoListItemElement.dataset.number;

            if (!number) {
                return;
            }

            infoListItemElement.addEventListener('mouseenter', () => {
                syncHoveredNumber(number);
            });

            infoListItemElement.addEventListener('mouseleave', () => {
                state.clearHighlightedNumbers();
            });
        });

        interactiveElements.infoPointElements.forEach((infoPointElement) => {
            const number = infoPointElement.dataset.number;

            if (!number) {
                return;
            }

            infoPointElement.addEventListener('mouseenter', () => {
                syncHoveredNumber(number);
            });

            infoPointElement.addEventListener('mouseleave', () => {
                state.clearHighlightedNumbers();
            });
        });
    }

    interactiveElements.infoPointElements.forEach((infoPointElement) => {
        const number = infoPointElement.dataset.number;

        if (!number) {
            return;
        }

        infoPointElement.addEventListener('click', (event) => {
            event.stopPropagation();
            toggleNumber(number);
        });
    });

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof HTMLElement)) {
            return;
        }

        const clickedInteractiveElement =
            event.target.closest(`.${configuration.blockClass}__info-point`) ||
            event.target.closest(`.${configuration.blockClass}__info-list-item`) ||
            event.target.closest(`.${configuration.blockClass}__panzoom-control-buttons`);

        if (clickedInteractiveElement) {
            return;
        }

        if (panZoomController.isActivationSuppressed()) {
            return;
        }

        state.clearActiveNumbers();
        state.clearHighlightedNumbers();
    });
};

export default useInteractiveMapInteractions;
